import { inject, Injectable } from '@angular/core';
import { orderBy, sumBy } from 'es-toolkit/compat';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AggregatedBoostersStatisticsDataV2 } from '#modules/statistics-boosters/models/aggregated-booster.interface';
import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { ChartSortBy } from '#shared/enums/sort.enum';
import { NfcToCsvMapper } from '#shared/helpers/index';

interface Data {
    chartsData: AggregatedBoostersStatisticsDataV2 | undefined;
    restaurants: LightRestaurant[];
}

interface AggregatedScanCountCsvInsight {
    location: string;
    locationInternalName: string;
    locationAddress: string;
    booster: string;
    scanCount: number;
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedBoostersScanCountCsvInsightV2Service extends AbstractCsvService<Data, Options> {
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _nfcToCsvMapper = inject(NfcToCsvMapper);

    protected _getData$({ restaurants }: Options): Observable<Data> {
        return of({
            restaurants,
            chartsData: this._aggregatedStatisticsStore.boosterStatsDataV2(),
        });
    }

    protected override _isDataValid({ chartsData, restaurants }: Data): boolean {
        return !!chartsData && restaurants.length > 0;
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Location', 'Location Internal Name', 'Location Address', 'Booster', 'Scan Count'];
    }

    protected override _getCsvDataRows(data: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const { chartsData, restaurants } = data;
        const groupedData: AggregatedScanCountCsvInsight[] = [];
        chartsData.nfcs.map((nfc) => {
            const restaurant = restaurants.find((r) => r.id === nfc.restaurantId);
            if (restaurant) {
                const nfcName = this._nfcToCsvMapper.mapNfcName(nfc);
                const scanCount = sumBy(
                    chartsData.scanCounts.filter(
                        (nfcScanCount) => nfc.id === nfcScanCount.nfcId && nfcScanCount.restaurantId === nfc.restaurantId
                    ),
                    'scanCount'
                );
                groupedData.push({
                    location: restaurant.name,
                    locationInternalName: restaurant.internalName ?? '',
                    locationAddress: restaurant.address?.formattedAddress ?? restaurant.address?.toString() ?? '-',
                    booster: nfcName,
                    scanCount,
                });
            }
        });

        return orderBy(groupedData, ['location'], [ChartSortBy.ASC]).map(
            ({ location, locationInternalName, locationAddress, booster, scanCount }) => [
                location,
                locationInternalName,
                locationAddress,
                booster,
                scanCount.toString(),
            ]
        );
    }
}
